import type { PricePillData } from "../../@types/mapviewxs";
import type { AvailableFilter, AvailableFilterOption, FilterKey } from "../map/types";
import { normalizePriceRange, parseAndValidatePrice } from "./utils";

export interface Input {
  open: boolean;
  itemPillData?: PricePillData;
  filterKey: FilterKey;
  filter?: AvailableFilter;
  minPrice?: number;
  maxPrice?: number;
  availableConditions?: string[];
  "on-close"?: () => void;
  "on-submit"?: (data: AvailableFilterOption[] | Partial<AvailableFilter>, filterKey: FilterKey) => void;
}

interface State {
  options: AvailableFilterOption[];
  minPriceInput: string;
  maxPriceInput: string;
  showMinError: boolean;
  showMaxError: boolean;
}

export default class extends Marko.Component<Input, State> {
  onCreate({ filter, filterKey }: Input) {
    const minValue = filterKey === "price" ? filter?.minValue : undefined;
    const maxValue = filterKey === "price" ? filter?.maxValue : undefined;

    this.state = {
      options: filter?.options || [],
      minPriceInput: minValue !== undefined ? String(minValue) : "",
      maxPriceInput: maxValue !== undefined ? String(maxValue) : "",
      showMinError: false,
      showMaxError: false,
    };
  }

  onInput(input: Input) {
    this.state.options = input.filter?.options || [];

    if (input.filterKey === "price" && input.filter) {
      const minValue = input.filter.minValue;
      const maxValue = input.filter.maxValue;

      this.state.minPriceInput = minValue !== undefined ? String(minValue) : "";
      this.state.maxPriceInput = maxValue !== undefined ? String(maxValue) : "";
      this.state.showMinError = false;
      this.state.showMaxError = false;
    }
  }

  removeUnavailableOptions(options: AvailableFilterOption[]): AvailableFilterOption[] {
    if (!this.input.availableConditions) {
      return options;
    }

    return options.filter((option) => {
      if (option.isEnabled) {
        return true;
      }

      return this.input.availableConditions?.includes(option.label);
    });
  }

  onOptionChange(e: { checked: boolean; value: string }) {
    const { checked, value } = e;

    const updatedOptions = this.state.options.map((option) => {
      if (option.fieldId === value) {
        return { ...option, isEnabled: checked };
      }
      return option;
    });

    this.state.options = !checked ? this.removeUnavailableOptions(updatedOptions) : updatedOptions;
  }

  handleMinChange(value: string) {
    this.state.minPriceInput = value;
    this.state.showMinError = false;
  }

  handleMaxChange(value: string) {
    this.state.maxPriceInput = value;
    this.state.showMaxError = false;
  }

  onFilterConditionReset() {
    this.state.options = this.state.options.map((option) => ({
      ...option,
      isEnabled: false,
    }));
  }

  onFilterPriceReset() {
    this.state.minPriceInput = "";
    this.state.maxPriceInput = "";
    this.state.showMinError = false;
    this.state.showMaxError = false;
  }

  onFilterConditionSubmit() {
    this.emit("submit", this.state.options, this.input.filterKey);
    this.emit("close");
  }

  onFilterPriceSubmit() {
    let hasError = false;

    if (this.state.minPriceInput !== "") {
      const minValue = parseAndValidatePrice(this.state.minPriceInput);
      if (minValue === undefined) {
        this.state.showMinError = true;
        hasError = true;
      }
    }

    if (this.state.maxPriceInput !== "") {
      const maxValue = parseAndValidatePrice(this.state.maxPriceInput);
      if (maxValue === undefined) {
        this.state.showMaxError = true;
        hasError = true;
      }
    }

    if (hasError) {
      return;
    }

    const minValue = this.state.minPriceInput ? parseAndValidatePrice(this.state.minPriceInput) : undefined;
    const maxValue = this.state.maxPriceInput ? parseAndValidatePrice(this.state.maxPriceInput) : undefined;

    const [finalMin, finalMax] = normalizePriceRange(minValue, maxValue);

    const priceFilter: Partial<AvailableFilter> = {
      minValue: finalMin,
      maxValue: finalMax,
    };

    this.emit("submit", priceFilter, this.input.filterKey);
    this.emit("close");
  }

  handleReset() {
    if (this.input.filterKey === "condition") {
      this.onFilterConditionReset();
    } else {
      this.onFilterPriceReset();
    }
  }

  handleSubmit() {
    if (this.input.filterKey === "condition") {
      this.onFilterConditionSubmit();
    } else {
      this.onFilterPriceSubmit();
    }
  }
}
